/**
 * Thawing tab — mirrors the Hot & Cold Hold tab pattern.
 *
 * v1 supports only "Running Water" method (chiller thawing is captured
 * by the CCP equipment temperature module). Duration ceiling defaults
 * to 120 min (SG SFA safe limit under 21 °C); any finish over that
 * flags as a breach and forces a corrective-action note. Supervisor
 * can then either verify (accept) or dispose (reject) the record.
 *
 * Bulk-create pattern: one modal accepts N product names at a shared
 * start time — the backend creates N sibling records with the same
 * batch_id so each product can be finished independently.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { useDialog } from "@/context/DialogProvider";
import api, { formatErr } from "@/lib/api";
import { Portal } from "@/components/Portal";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, ChevronRight, Clock, Droplets,
  Plus, RefreshCw, Trash2, X,
} from "lucide-react";

const VERIFY_ROLES = ["owner", "manager", "supervisor"];
const STATUS_LABEL = {
  in_progress: "In progress",
  pending_verify: "Awaiting verification",
  verified: "Verified",
  disposed: "Disposed",
};

// ── datetime helpers (kept local to this file) ──
const dtLocalNow = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};
const toISO = (v) => (v ? new Date(v).toISOString() : null);
const fmt = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString(undefined,
      { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return v; }
};
const durationLabel = (mins) => {
  if (mins == null) return "—";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins - h * 60);
  return m === 0 ? `${h} h` : `${h} h ${m} m`;
};

function StatusPill({ status }) {
  const cls = status === "verified" ? "ok"
    : status === "pending_verify" ? "warn"
    : status === "disposed" ? "err"
    : "";
  return (
    <span className={`vb-pill ${cls} text-[10px]`}
      data-testid={`thaw-status-${status}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}
function BreachPill({ breach }) {
  return breach
    ? <span className="vb-pill err text-[10px]"><AlertTriangle size={11} /> Over 2h</span>
    : <span className="vb-pill ok text-[10px]"><CheckCircle2 size={11} /> OK</span>;
}
function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-xs uppercase font-semibold opacity-70">{label}</label>
      {children}
      {hint && <p className="text-[11px] opacity-60 mt-0.5">{hint}</p>}
    </div>
  );
}

function useKitchenFacilities() {
  const { activeSiteId } = useSite();
  const [facilities, setFacilities] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/thawing/facilities")
      .then(({ data }) => { if (!cancelled) setFacilities(data.facilities || []); })
      .catch(() => { if (!cancelled) setFacilities([]); });
    return () => { cancelled = true; };
  }, [activeSiteId]);
  return facilities;
}

function useSiteUsers() {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    // Reuse the HCH endpoint — same tenant, same scoping.
    api.get("/hot-cold-hold/site-users")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => setUsers([]));
  }, []);
  return users;
}

/* ===================== main tab ===================== */
export default function ThawingTab({ canVerify, needsSite, noFacility }) {
  const { activeSiteId } = useSite();
  const { confirm } = useDialog();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [finishing, setFinishing] = useState(null);   // record being finished
  const [config, setConfig] = useState({ max_duration_min: 120, method: "running_water" });

  useEffect(() => {
    api.get("/thawing/config").then(({ data }) =>
      setConfig(data.config || { max_duration_min: 120, method: "running_water" })
    ).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const { data } = await api.get(`/thawing/records?status=${statusFilter}`);
    setRows(data.records || []);
  }, [statusFilter]);
  useEffect(() => { load(); }, [load, activeSiteId]);

  const del = async (r) => {
    if (!await confirm({
      title: "Delete this thawing record?",
      message: "Verified and disposed records cannot be deleted.",
      danger: true, confirmLabel: "Delete",
    })) return;
    try {
      await api.delete(`/thawing/records/${r.id}`);
      toast.success("Deleted"); setSelected(null); load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Delete failed");
    }
  };
  const verify = async (r) => {
    try {
      await api.put(`/thawing/records/${r.id}/verify`, { notes: "" });
      toast.success("Verified & locked");
      setSelected(null); load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Verify failed");
    }
  };
  const dispose = async (r, reason) => {
    try {
      await api.put(`/thawing/records/${r.id}/dispose`, { reason });
      toast.success("Marked disposed");
      setSelected(null); load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Dispose failed");
    }
  };

  const options = ["all", "in_progress", "pending_verify", "verified", "disposed"];

  return (
    <div data-testid="thawing-tab">
      <div className="flex justify-end mb-3">
        <button className="vb-btn" onClick={() => setShowNew(true)}
          disabled={needsSite || noFacility} data-testid="new-thaw"
          title={noFacility ? "Add a food-production facility to this site before logging a thaw." : undefined}>
          <Plus size={16} /> New thaw
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {options.map((s) => (
          <button key={s} className={`vb-pill ${statusFilter === s ? "ok" : ""}`}
            style={{ cursor: "pointer" }} onClick={() => setStatusFilter(s)}
            data-testid={`thaw-filter-${s}`}>
            {s === "all" ? "All" : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <div className="vb-card overflow-x-auto">
        <table className="vb-table">
          <thead>
            <tr>
              <th>Product · <span className="opacity-60 font-normal">产品名称</span></th>
              <th>Method · <span className="opacity-60 font-normal">方法</span></th>
              <th>Start · <span className="opacity-60 font-normal">开始时间</span></th>
              <th>End · <span className="opacity-60 font-normal">结束时间</span></th>
              <th>Duration · <span className="opacity-60 font-normal">共时</span></th>
              <th>Status</th>
              <th>Flag</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 28 }}
                  className="opacity-60">
                  No thawing records yet. Click <em>New thaw</em> to log one.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const canFinish = r.status === "in_progress";
              return (
                <tr key={r.id} data-testid={`thaw-row-${r.id}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelected(r)}>
                  <td><strong>{r.product_name}</strong>
                    <div className="opacity-60 text-[11px]">
                      {r.facility_name || "—"}
                    </div>
                  </td>
                  <td>
                    <span className="inline-flex items-center gap-1"
                      style={{ color: "#2563eb" }}>
                      <Droplets size={13} /> Running water
                    </span>
                  </td>
                  <td className="opacity-70">{fmt(r.start_at)}</td>
                  <td className="opacity-70">{fmt(r.end_at)}</td>
                  <td>{durationLabel(r.duration_min)}</td>
                  <td><StatusPill status={r.status} /></td>
                  <td>{r.end_at ? <BreachPill breach={r.breach} /> : <span className="opacity-40 text-[10px]">—</span>}</td>
                  <td className="text-right">
                    {canFinish && (
                      <button className="vb-btn secondary text-xs"
                        onClick={(e) => { e.stopPropagation(); setFinishing(r); }}
                        data-testid={`finish-thaw-${r.id}`}>
                        <Clock size={13} /> Finish
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewThawModal config={config}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }} />
      )}
      {finishing && (
        <FinishThawModal record={finishing} config={config}
          onClose={() => setFinishing(null)}
          onSaved={() => { setFinishing(null); load(); }} />
      )}
      {selected && (
        <ThawDetailDrawer record={selected} canVerify={canVerify}
          onClose={() => setSelected(null)}
          onVerify={() => verify(selected)}
          onDispose={(reason) => dispose(selected, reason)}
          onDelete={() => del(selected)} />
      )}
    </div>
  );
}

/* ===================== new-thaw modal (multi-item) ===================== */
function NewThawModal({ config, onClose, onSaved }) {
  const { user: me } = useAuth();
  const users = useSiteUsers();
  const facilities = useKitchenFacilities();
  const [facilityId, setFacilityId] = useState("");
  const [recBy, setRecBy] = useState({ id: me?.id || "", name: me?.name || "" });
  const [startAt, setStartAt] = useState(dtLocalNow());
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ product_name: "" }]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!facilityId && facilities.length === 1) setFacilityId(facilities[0].id);
  }, [facilities, facilityId]);

  const setItem = (i, value) =>
    setItems((xs) => xs.map((x, idx) => (idx === i ? { product_name: value } : x)));
  const addItem = () => setItems((xs) => [...xs, { product_name: "" }]);
  const removeItem = (i) =>
    setItems((xs) => (xs.length > 1 ? xs.filter((_, idx) => idx !== i) : xs));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const cleaned = items
        .map((x) => ({ product_name: x.product_name.trim() }))
        .filter((x) => x.product_name.length > 0);
      if (cleaned.length === 0) throw new Error("Add at least one product name.");
      await api.post("/thawing/records", {
        facility_id: facilityId,
        start_at: toISO(startAt),
        recorded_by: recBy.name,
        recorded_by_id: recBy.id,
        notes,
        items: cleaned,
      });
      toast.success(`${cleaned.length} thaw record${cleaned.length > 1 ? "s" : ""} started`);
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message || "Save failed");
    } finally { setBusy(false); }
  };

  const maxDur = config?.max_duration_min ?? 120;

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={onClose}>
        <form className="vb-modal p-5 w-full max-w-2xl"
          data-testid="new-thaw-modal"
          onClick={(e) => e.stopPropagation()} onSubmit={submit}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="vb-heading text-xl">New Thawing Record</h3>
              <p className="text-xs opacity-70 mt-0.5">
                Method locked to <strong>Running Water</strong>. Safe limit
                is <strong>{maxDur} min</strong>; anything longer will
                flag as a breach and require a corrective action.
              </p>
            </div>
            <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100">
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Kitchen (facility)"
              hint={facilities.length === 0
                ? "No food-production facility set up for this site — ask your owner to add one under Sites."
                : "Only food-production facilities are shown."}>
              <select className="vb-input" required value={facilityId}
                onChange={(e) => setFacilityId(e.target.value)}
                data-testid="thaw-facility">
                <option value="" disabled>Pick a kitchen</option>
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Start date & time" hint="Prefilled to now — adjust if back-dating.">
              <input className="vb-input" type="datetime-local" required
                value={startAt} onChange={(e) => setStartAt(e.target.value)}
                data-testid="thaw-start-at" />
            </Field>
          </div>

          <div className="mt-3">
            <label className="text-xs uppercase font-semibold opacity-70">
              Products being thawed <span className="opacity-50">· 产品名称</span>
            </label>
            <p className="text-[11px] opacity-60 mb-2">
              Add every product going into the running-water bath now.
              Each will become its own record — you can finish them
              individually as they thaw.
            </p>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="flex gap-2 items-center"
                  data-testid={`thaw-item-row-${i}`}>
                  <span className="opacity-40 text-xs w-6 text-right">
                    {i + 1}.
                  </span>
                  <input className="vb-input flex-1"
                    placeholder="e.g. Salmon fillet 500g"
                    value={it.product_name}
                    onChange={(e) => setItem(i, e.target.value)}
                    data-testid={`thaw-item-name-${i}`} />
                  <button type="button" className="vb-btn ghost"
                    onClick={() => removeItem(i)}
                    disabled={items.length === 1}
                    data-testid={`thaw-item-remove-${i}`}
                    style={{ padding: "6px 8px" }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="vb-btn ghost text-xs mt-2"
              onClick={addItem} data-testid="thaw-add-item">
              <Plus size={13} /> Add another product
            </button>
          </div>

          <div className="mt-3">
            <Field label="Checked by"
              hint="Defaults to you; pick another if you're logging for a colleague.">
              <select className="vb-input"
                value={recBy.id}
                onChange={(e) => {
                  const u = users.find((x) => x.id === e.target.value);
                  setRecBy({ id: u?.id || "", name: u?.name || "" });
                }}
                data-testid="thaw-checked-by">
                {users.length === 0 && (
                  <option value={me?.id}>{me?.name || me?.email}</option>
                )}
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="mt-3">
            <Field label="Notes (optional)">
              <textarea className="vb-input" rows={2} value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="thaw-notes" />
            </Field>
          </div>

          {err && <div className="vb-pill err mt-3" data-testid="thaw-form-error">{err}</div>}

          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy}
              data-testid="thaw-submit">
              {busy ? "Saving…" : "Start thawing"}
            </button>
          </div>
        </form>
      </div>
    </Portal>
  );
}

/* ===================== finish-thaw modal ===================== */
function FinishThawModal({ record, config, onClose, onSaved }) {
  const [endAt, setEndAt] = useState(dtLocalNow());
  const [corrective, setCorrective] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Live breach preview
  const maxDur = record.max_duration_min || config?.max_duration_min || 120;
  const start = new Date(record.start_at);
  const end = new Date(endAt);
  const durationMin = Math.max(0, Math.round((end - start) / 60000));
  const breach = durationMin > maxDur;

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post(`/thawing/records/${record.id}/finish`, {
        end_at: toISO(endAt),
        corrective_action: corrective,
      });
      toast.success(breach
        ? "Finished — flagged for supervisor sign-off"
        : "Finished — sent for verification");
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || "Finish failed");
    } finally { setBusy(false); }
  };

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={onClose}>
        <form className="vb-modal p-5 w-full max-w-lg"
          data-testid="finish-thaw-modal"
          onClick={(e) => e.stopPropagation()} onSubmit={submit}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="vb-heading text-xl">Finish thawing</h3>
              <p className="text-xs opacity-70 mt-0.5">
                <strong>{record.product_name}</strong> · started {fmt(record.start_at)}
              </p>
            </div>
            <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100">
              <X size={18} />
            </button>
          </div>

          <Field label="End date & time"
            hint="Prefilled to now — adjust if the thaw actually finished earlier.">
            <input className="vb-input" type="datetime-local" required
              value={endAt} onChange={(e) => setEndAt(e.target.value)}
              data-testid="thaw-end-at" />
          </Field>

          <div className="mt-3 p-3 rounded-lg" style={{
            background: breach ? "rgba(220,38,38,0.07)" : "rgba(34,197,94,0.07)",
            border: `1px solid ${breach ? "rgba(220,38,38,0.3)" : "rgba(34,197,94,0.3)"}`,
          }} data-testid="thaw-duration-preview">
            <div className="text-sm flex items-center gap-2">
              <Clock size={14} />
              <span>Duration: <strong>{durationLabel(durationMin)}</strong></span>
              <span className="opacity-60">· safe limit {maxDur} min</span>
            </div>
            {breach && (
              <div className="text-xs mt-1" style={{ color: "#b91c1c" }}>
                Over the {maxDur}-minute safe limit — this record will be
                flagged as a breach.
              </div>
            )}
          </div>

          {breach && (
            <div className="mt-3 p-3 rounded-lg" style={{
              background: "rgba(220,38,38,0.07)",
              border: "1px solid rgba(220,38,38,0.3)",
            }} data-testid="thaw-corrective-box">
              <div className="flex items-center gap-2 text-sm font-semibold mb-1"
                style={{ color: "#b91c1c" }}>
                <AlertTriangle size={14} /> Corrective action required
              </div>
              <p className="text-[11px] mb-2" style={{ color: "#991b1b" }}>
                What did you do with this product? (e.g. discarded and
                re-thawed a fresh pack, cooked immediately from partially
                thawed, etc.) Supervisor will review.
              </p>
              <textarea className="vb-input" rows={3}
                required
                value={corrective}
                onChange={(e) => setCorrective(e.target.value)}
                data-testid="thaw-corrective-input" />
            </div>
          )}

          {err && <div className="vb-pill err mt-3" data-testid="thaw-finish-error">{err}</div>}

          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="vb-btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy}
              data-testid="thaw-finish-submit">
              {busy ? "Saving…" : "Finish & send for sign-off"}
            </button>
          </div>
        </form>
      </div>
    </Portal>
  );
}

/* ===================== detail drawer ===================== */
function ThawDetailDrawer({ record, canVerify, onClose, onVerify, onDispose, onDelete }) {
  const { user: me } = useAuth();
  const [disposing, setDisposing] = useState(false);
  const [reason, setReason] = useState("");

  // Delete is a supervisor+ action (mirrors HCH). Staff can create + finish
  // but not remove — otherwise a slip-up masquerades as a valid record.
  const canDelete = canVerify
    && (record.status === "in_progress" || record.status === "pending_verify");
  const canAct = canVerify && record.status === "pending_verify";

  return (
    <Portal>
      <div className="vb-modal-bg" onClick={onClose}>
        <div className="vb-modal p-5 w-full max-w-xl"
          data-testid="thaw-detail-drawer"
          onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="vb-heading text-xl">{record.product_name}</h3>
              <p className="text-xs opacity-70 mt-0.5">
                {record.facility_name || "—"}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <StatusPill status={record.status} />
                {record.end_at && <BreachPill breach={record.breach} />}
              </div>
            </div>
            <button onClick={onClose} className="opacity-60 hover:opacity-100">
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="opacity-60">Method</span><br />Running water · 流水</div>
            <div><span className="opacity-60">Started by</span><br />{record.recorded_by || "—"}</div>
            <div><span className="opacity-60">Start</span><br />{fmt(record.start_at)}</div>
            <div><span className="opacity-60">End</span><br />{fmt(record.end_at)}</div>
            <div><span className="opacity-60">Duration</span><br />{durationLabel(record.duration_min)}</div>
            <div><span className="opacity-60">Safe limit</span><br />{record.max_duration_min || 120} min</div>
            {record.finish_recorded_by && (
              <div className="col-span-2">
                <span className="opacity-60">Finished by</span><br />
                {record.finish_recorded_by}
              </div>
            )}
          </div>

          {record.notes && (
            <div className="mt-3">
              <div className="text-xs uppercase opacity-70 mb-1">Notes</div>
              <div className="text-sm opacity-80">{record.notes}</div>
            </div>
          )}

          {record.corrective_action && (
            <div className="mt-3 p-3 rounded-lg" style={{
              background: "rgba(220,38,38,0.07)",
              border: "1px solid rgba(220,38,38,0.3)",
            }}>
              <div className="text-xs uppercase font-semibold mb-1"
                style={{ color: "#b91c1c" }}>Corrective action</div>
              <div className="text-sm" style={{ color: "#991b1b" }}>
                {record.corrective_action}
              </div>
            </div>
          )}

          {record.verification && (
            <div className="mt-3 p-3 rounded-lg" style={{
              background: "rgba(34,197,94,0.07)",
              border: "1px solid rgba(34,197,94,0.3)",
            }}>
              <div className="text-xs uppercase font-semibold mb-1"
                style={{ color: "#15803d" }}>Verified</div>
              <div className="text-sm">
                {record.verification.verified_by_name} · {fmt(record.verification.verified_at)}
                {record.verification.notes && (
                  <div className="opacity-70 mt-1">{record.verification.notes}</div>
                )}
              </div>
            </div>
          )}

          {record.disposed && (
            <div className="mt-3 p-3 rounded-lg" style={{
              background: "rgba(120,120,120,0.08)",
              border: "1px solid rgba(120,120,120,0.35)",
            }}>
              <div className="text-xs uppercase font-semibold mb-1">Disposed</div>
              <div className="text-sm">
                {record.disposed.disposed_by_name} · {fmt(record.disposed.disposed_at)}
                <div className="opacity-70 mt-1">
                  Reason: {record.disposed.reason}
                </div>
              </div>
            </div>
          )}

          {disposing && (
            <div className="mt-3 p-3 rounded-lg" style={{
              background: "rgba(120,120,120,0.08)",
              border: "1px solid rgba(120,120,120,0.35)",
            }}>
              <div className="text-xs uppercase font-semibold mb-1">
                Dispose this product
              </div>
              <p className="text-[11px] opacity-70 mb-2">
                Rejecting means the food should NOT be served. This locks
                the record.
              </p>
              <textarea className="vb-input" rows={2}
                placeholder="Reason (e.g. Left too long — food unsafe)"
                value={reason} onChange={(e) => setReason(e.target.value)}
                data-testid="thaw-dispose-reason" />
              <div className="flex justify-end gap-2 mt-2">
                <button className="vb-btn ghost text-xs" onClick={() => setDisposing(false)}>
                  Cancel
                </button>
                <button className="vb-btn danger text-xs"
                  disabled={!reason.trim()}
                  onClick={() => { onDispose(reason.trim()); setDisposing(false); }}
                  data-testid="thaw-dispose-confirm">
                  Confirm dispose
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4 flex-wrap">
            {canDelete && (
              <button className="vb-btn ghost text-xs" onClick={onDelete}
                data-testid="thaw-delete">
                <Trash2 size={13} /> Delete
              </button>
            )}
            {canAct && record.breach && !disposing && (
              <button className="vb-btn danger text-xs"
                onClick={() => setDisposing(true)}
                data-testid="thaw-open-dispose">
                <AlertTriangle size={13} /> Dispose
              </button>
            )}
            {canAct && (
              <button className="vb-btn text-xs" onClick={onVerify}
                data-testid="thaw-verify">
                <CheckCircle2 size={13} /> Verify &amp; lock
              </button>
            )}
            <button className="vb-btn secondary text-xs" onClick={onClose}>
              Close <ChevronRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
